import { useCallback, useEffect, useRef, useState, type SetStateAction } from 'react';
import { api, ApiError, errorText } from './client';

/**
 * GET with loading/error state; `reload()` refetches quietly (no loading flash), `retry()` shows loading.
 * When the path changes the previous data is cleared, so one record never shows under another URL;
 * lists whose filters change pass `keepPrevious` to keep the old rows visible while the new page loads.
 */
/** `select` maps the backend's answer (`W`) to the screen's model (`T`) at the API boundary. */
export function useApi<T, W = T>(path: string | null, opts: { keepPrevious?: boolean; select?: (raw: W) => T } = {}) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** the failure's code (ERROR_CODE), for a caller that acts on why it failed (e.g. NOT_FOUND) */
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(!!path);
  const seq = useRef(0);
  const shownPath = useRef(path);
  const keep = !!opts.keepPrevious;
  // an answer mapped to the screen's model at the API boundary (the backend's shape stays in lib/api)
  const select = useRef(opts.select);
  select.current = opts.select;
  if (shownPath.current !== path) {
    shownPath.current = path;
    setLoading(!!path);
    if (!keep) {
      setData(null);
      setError(null);
      setErrorCode(null);
    }
  }
  const load = useCallback(
    async (quiet = false) => {
      const my = ++seq.current;
      if (!path) {
        setLoading(false);
        return;
      }
      if (!quiet) setLoading(true);
      try {
        const raw = await api<unknown>(path);
        const d = select.current ? select.current(raw as W) : (raw as T);
        if (my === seq.current) {
          setData(d);
          setError(null);
          setErrorCode(null);
        }
      } catch (e) {
        if (my === seq.current) {
          setError(errorText(e));
          setErrorCode(e instanceof ApiError ? e.code : null);
        }
      } finally {
        if (my === seq.current) setLoading(false);
      }
    },
    [path],
  );
  useEffect(() => {
    void load();
  }, [load]);
  const reload = useCallback(() => load(true), [load]);
  const retry = useCallback(() => load(false), [load]);
  /** A local change (an optimistic update, a mutation's answer) is newer than any load still on its way: those are dropped. */
  const set = useCallback((v: SetStateAction<T | null>) => {
    seq.current++;
    setLoading(false);
    setData(v);
  }, []);
  /**
   * A live change to the data on screen (a stream message about one row): a load on its way still lands, since it
   * is a fresher, whole answer (a new filter, a page step); `fn` returns its argument when nothing changed.
   */
  const patch = useCallback((fn: (d: T) => T) => setData((d) => (d === null ? d : fn(d))), []);
  return { data, error, errorCode, loading, reload, retry, setData: set, patch };
}

/** What `useApi` returns, for components that receive one. */
export type ApiState<T> = ReturnType<typeof useApi<T>>;
